// Libraries
import React, { useState, useContext } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import _ from 'lodash';
import Highlighter from 'react-highlight-words';
import {
  Card,
  Box,
  ButtonGroup,
  CardContent,
  IconButton,
  Collapse,
  Typography,
  // Tooltip,
  Stack,
  darken,
  lighten,
  Menu,
  MenuItem,
} from '@mui/material';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import DisabledByDefaultOutlinedIcon from '@mui/icons-material/DisabledByDefaultOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FormatQuoteOutlinedIcon from '@mui/icons-material/FormatQuoteOutlined';
// import ExpandLess from '@mui/icons-material/ExpandLess';
// import ExpandMore from '@mui/icons-material/ExpandMore';
// import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Button } from '@mui/material';

// Local
import { SUMMARIZE, SINGLE_PAPER, CITE_PAPER } from '../../../Queries/nlp';
import { SAVE_BOOKMARK } from '../../../Queries/document';
import { POST_MANUSCRIPT_LIBRARY } from '../../../../Queries'
import { DocumentContext } from '../../../DocumentContext';
import { notify } from '../../../../Common/notify';
import { cite } from '../../../Editor/Utilities/cite';
import { ModularContext } from '../../../../Modular/ModularContext';
import AbstractParsed from '../../View/Document/AbstractParsed';
import CitationExport from '../../Common/CitationExport';
import { addPaperToZotero } from '../../../Requests/Document'
import Scores from './Scores';
import CollectionList from '../../../../Account/Zotero/CollectionList';
import GlobalModal from '../../../../Common/GlobalModal';
import ZoteroIcon from '../../../../Zotero/ZoteroIcon';
import RemovePromptNoteLibrary from './RemovePromptNoteLibrary';

const SearchResult = ({ client, paper, user, modular }) => {
  const contextSource = modular ? ModularContext : DocumentContext;
  const context = useContext(contextSource);

  const [collapsed, collapsedToggle] = useState(false);
  const [modal, setModal] = useState(false);
  const [bibtex, setBibtex] = useState('');
  const [mla, setMla] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [refresher, setRefresher] = useState(0);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [modalRemovePromptNoteLibrary, setModalRemovePromptNoteLibrary] = useState(false);
  const open = Boolean(anchorEl);

  const setLoadingRequest = (loading) => {
    context.updateContext('loadingRequest', loading);
  }

  const setLoadingMessage = (message) => {
    context.updateContext('loadingMessage', message);
  }

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const [saveBookmark] = useMutation(SAVE_BOOKMARK, {
    fetchPolicy: 'network-only',
  });

  const [postManuscriptLibrary] = useMutation(POST_MANUSCRIPT_LIBRARY, {
    fetchPolicy: 'network-only',
  });

  const [summarizePaper] = useLazyQuery(SUMMARIZE, {
    fetchPolicy: 'network-only',
    onCompleted: ({ summarizePaper }) => {
      context.updateContext('summaries', summarizePaper.response);
    },
  });

  const [singlePaper] = useLazyQuery(SINGLE_PAPER, {
    fetchPolicy: 'network-only',
  });

  const [citePaper] = useLazyQuery(CITE_PAPER, {
    fetchPolicy: 'network-only',
    onCompleted: ({ citePaper }) => {
      if (citePaper.status === 'success') {
        setBibtex(citePaper.response.bibtex);
        setMla(citePaper.response.mla);
        setIsOpen(true);
        context.updateContext('loadingRequest', false);
        context.updateContext('loadingMessage', 'Retrieved citations');
      } else {
        notify('Could not generate citations, try again later');
        context.updateContext('loadingRequest', false);
        context.updateContext('loadingMessage', '');
      }
    },
  });

  const uploadPaperToZotero = (client, paper) => {
    const {
      collection,
      id_value: idValue,
      id_field: idField,
      id_type: idType,
    } = context.paperIds.find(
      (meta) => meta.id_value === paper.id_int.toString()
    );

    const paperMetadataToAddToZotero = {
      collection: collection,
      id_field: idField,
      id_value: idValue,
      id_type: idType,
    };

    context.updateContext('loadingRequest', true);
    context.updateContext('loadingMessage', 'Adding paper to Zotero');
    addPaperToZotero(
      client,
      {
        zoteroCollectionId: context.user.zoteroCollectionName,
        paper: paperMetadataToAddToZotero,
      },
      setLoadingRequest,
      setLoadingMessage
    );
  }

  return (
    <>
    <GlobalModal
      isOpen={modal}
      setOpen={setModal}
      heading={'Zotero Collections'}
      subheading={'Select Zotero collection to upload paper'}
      aria-label={"global-modal"}
    >
      <CollectionList setModal={setModal}/>
    </GlobalModal>
    <RemovePromptNoteLibrary
        paper={paper}
        modal={modalRemovePromptNoteLibrary}
        setModal={setModalRemovePromptNoteLibrary}
        client={client}
      />
    <Card
      sx={{
        backgroundColor: (theme) =>
          theme.palette.mode === 'light' ? 'background.paper' : '#171717',
        width: '100%',
        '&:hover': {
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? darken(theme.palette.background.paper, 0.05)
              : lighten('#171717', 0.05),
        },
      }}
      elevation={0}
    >
      <CitationExport
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        bibtex={bibtex}
        setBibtex={setBibtex}
        mla={mla}
        setMla={setMla}
      />
      <CardContent
        sx={{
          padding: '10px',
          ':last-child': {
            paddingBottom: '5px',
          },
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            sm: 'column',
            md: 'row',
          }}
          sx={{
            width: '100%',
            justifyContent: 'space-between',
            boxSizing: 'border-box',
            alignItems: 'flex-start',
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              marginBottom: '0px',
              boxSizing: 'border-box',
              maxWidth: '100%',
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'cornflowerblue',
                fontWeight: 'bold',
                cursor: 'pointer',
                width: '100%',
                ':hover': {
                  textDecoration: 'underline',
                },
              }}
              onMouseDown={() => {
                if (modular) {
                  const idValue = paper.id_int.toString();

                  const metadata = _.find(context.metadataResults, {
                    id_value: idValue,
                  });

                  singlePaper({
                    variables: {
                      paper_id: {
                        collection: metadata.collection,
                        id_value: metadata.id_value,
                        id_field: metadata.id_field,
                        id_type: metadata.id_type,
                      },
                    },
                    onCompleted: ({ singlePaper }) => {
                      if (singlePaper.status === 'success') {
                        const windowId = context.onAddWindow('Read');

                        const newPaperList = context.papers;
                        newPaperList[windowId] = {
                          window_id: windowId,
                          ...singlePaper.response,
                        };

                        context.updateContext('papers', newPaperList);

                        // make paperTitles in Generation module a context variable, update it from here

                        const newPaperTitles = Object.values(newPaperList).map(
                          (p) => p.Title
                        );

                        context.updateContext('paperTitles', newPaperTitles);

                        const newPaperMetadata = context.papersMetadata;
                        newPaperMetadata[windowId] = {
                          window_id: windowId,
                          ...metadata,
                        };

                        context.updateContext(
                          'papersMetadata',
                          newPaperMetadata
                        );

                        let highlightScope = context.highlightPapers;
                        highlightScope = {
                          rankingIdValue: metadata.id_value,
                          rankingIdField: metadata.id_field,
                          rankingIdType: metadata.id_type,
                          rankingCollection: metadata.collection,
                        };

                        context.updateContext(
                          'highlightPapers',
                          highlightScope
                        );

                        // add window to textAlign object
                        const newTextAlign = context.textAlign;
                        newTextAlign[windowId] = {
                          source: '',
                          isTextAlign: false,
                          sentences: [],
                          title: paper.Title,
                        };
                        context.updateContext('textAlign', newTextAlign);
                      } else {
                        notify('cannot retrieve the paper, try later!');
                      }
                    },
                  });
                } else {
                  context.updateContext('loadingRequest', true);
                  context.updateContext('loadingMessage', 'Retrieving paper');
                  context.updateContext('sortedView', false);

                  const {
                    collection,
                    id_value: idValue,
                    id_field: idField,
                    id_type: idType,
                  } = context.paperIds.find(
                    (meta) => meta.id_value === paper.id_int.toString()
                  );

                  context.updateContext('rankingDetails', {
                    rankingIdValue: idValue,
                    rankingIdField: idField,
                    rankingIdType: idType,
                    rankingCollection: collection,
                  });

                  summarizePaper({
                    variables: {
                      paper_id: {
                        collection,
                        id_value: idValue,
                        id_field: idField,
                        id_type: idType,
                      },
                    },
                  });

                  singlePaper({
                    variables: {
                      paper_id: {
                        collection,
                        id_value: idValue,
                        id_field: idField,
                        id_type: idType,
                      },
                    },
                    onCompleted: ({ singlePaper }) => {
                      if (singlePaper.status === 'success') {
                        context.updateContext('paper', singlePaper.response);
                        context.updateContext('tab', 'View');
                        context.updateContext('showSelection', false);
                        context.updateContext('highlightSelection', '');
                        context.updateContext('loadingRequest', false);
                        context.updateContext(
                          'loadingMessage',
                          'Retrieved paper'
                        );
                        context.recordActionHistory({
                          collection,
                          id_value: idValue,
                          id_field: idField,
                          id_type: idType,
                        });
                      } else {
                        notify('Could not retrieve paper, try again later');
                        context.updateContext('loadingRequest', false);
                        context.updateContext('loadingMessage', '');
                      }
                    },
                  });
                }
              }}
            >
              {paper.Title}
              <Scores paper={paper} />
            </Typography>
            {paper.Author.slice(0, 3).map((author, index) => (
              <Typography
                variant="caption"
                sx={{
                  marginLeft: '0px !important',
                  marginRight: index < 2 ? '5px !important' : '0px',
                }}
                key={index}
              >
                {`${author.FamilyName}, ${
                  author.GivenName.endsWith('.')
                    ? author.GivenName
                    : author.GivenName + '.'
                }`}
              </Typography>
            ))}
            {paper.Author.length > 3 && (
              <Typography
                variant="caption"
                sx={{
                  marginLeft: '0px !important',
                  marginRight: '5px !important',
                }}
              >
                ...
              </Typography>
            )}
            {paper.Venue && (
              <Typography variant="caption" sx={{ display: 'inline' }}>
                {paper.Author.length >= 1 && (
                  <span
                    style={{
                      marginRight: '5px',
                    }}
                  >
                    •
                  </span>
                )}
                <span
                  style={{
                    textDecoration: 'underline',
                    marginLeft: paper.Author.length >= 1 ? '0px' : '0px',
                  }}
                >
                  {paper.Venue}
                </span>
              </Typography>
            )}
            {paper.PublicationDate.Year && (
              <Typography
                variant="caption"
                sx={{
                  marginLeft: '0px !important',
                  marginRight: '5px !important',
                }}
              >
                {`${paper.Author.length >= 1 || paper.Venue
                    ? ' • ' + paper.PublicationDate.Year
                    : paper.PublicationDate.Year
                }`}
              </Typography>
            )}
          </Stack>

          <ButtonGroup
            fullWidth={false}
            aria-label="small button group"
            sx={{
              marginLeft: { xs: '0px', md: '20px' },
              maxHeight: '40px',
              marginBottom: '10px',
              width: 'auto',
            }}
          >
            <IconButton
              aria-label="more"
              aria-controls={open ? 'paper-menu' : undefined}
              aria-haspopup="true"
              onClick={handleMenuClick}
              size="small"
            >
              <MoreHorizIcon fontSize='small' color='primary' />
            </IconButton>
            <Menu
              id="paper-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              MenuListProps={{
                'aria-labelledby': 'more-button',
              }}
            >
              <MenuItem
                disabled={context.readOnly}
                onClick={async () => {
                  handleMenuClose();
                  if (context.readOnly) return;

                  if (!modular) {
                    context.updateContext('loadingRequest', true);
                    context.updateContext('loadingMessage', 'Citing paper');
                  }

                  const {
                    collection,
                    id_value: idValue,
                    id_field: idField,
                    id_type: idType,
                  } = context[modular ? 'metadataResults' : 'paperIds'].find(
                    (meta) => meta.id_value === paper.id_int.toString()
                  );

                  singlePaper({
                    variables: {
                      paper_id: {
                        collection,
                        id_value: idValue,
                        id_field: idField,
                        id_type: idType,
                      },
                    },
                    onCompleted: ({ singlePaper }) => {
                      if (singlePaper.status === 'success') {
                        cite(
                          context.editorRef,
                          singlePaper.response,
                          {
                            collection,
                            id_value: idValue,
                            id_field: idField,
                            id_type: idType,
                          },
                          context.autoAppearanceOrder,
                          context.showNumbering
                        );

                        if (!modular) {
                          context.updateContext('loadingRequest', false);
                          context.updateContext(
                            'loadingMessage',
                            'Cited paper'
                          );
                        }
                      } else {
                        notify('Could not retrieve paper, try again later');
                        if (!modular) {
                          context.updateContext('loadingRequest', false);
                          context.updateContext('loadingMessage', '');
                        }
                      }
                    },
                  });
                }}
              >
                <FormatQuoteOutlinedIcon sx={{ width: 16, height: 16, marginRight: '10px' }} />
                Cite paper in note
              </MenuItem>

              <MenuItem
                onClick={async () => {
                  handleMenuClose();
                  if (user.zoteroUserID !== '') { // if zotero connected 
                    if (context.user.zoteroCollectionName === '') { // if no default collection is selected
                      notify("Please first select a Zotero collection. Go to Panel and then Select Collection")
                    } else {
                      uploadPaperToZotero(client, paper)
                    }
                  } else {
                    notify("Please connect to Zotero first")
                  }
                }}
              >
                <ZoteroIcon />
                Add paper to Zotero
              </MenuItem>
              
              { !context.bookmarks.some(bookmark => bookmark.id_int === paper.id_int) ? 
                <MenuItem
                  onClick={async (e) => {
                    handleMenuClose();
                    e.preventDefault();

                    if (!modular) {
                      context.updateContext('loadingRequest', true);
                      context.updateContext('loadingMessage', 'Adding to library');
                    }

                    const {
                      collection,
                      id_value: idValue,
                      id_field: idField,
                      id_type: idType,
                    } = context[modular ? 'metadataResults' : 'paperIds'].find(
                      (meta) => meta.id_value === paper.id_int.toString()
                    );

                    saveBookmark({
                      variables: {
                        id_collection: collection,
                        id_value: idValue,
                        id_field: idField,
                        id_type: idType,
                      },
                      onCompleted: ({ saveBookmark }) => {
                        if (saveBookmark.status === 'success') {
                          const updatedBookmarks = context.bookmarks;
                          const bookmarkID = saveBookmark.response._id;

                          updatedBookmarks.push({
                            _id: bookmarkID,
                            abstract: paper.Content.Abstract,
                            abstract_parsed: paper.Content.Abstract_Parsed,
                            doi: paper.DOI,
                            id_int: paper.id_int,
                            title: paper.Title,
                            id_collection: collection,
                            id_field: idField,
                            id_type: idType,
                            id_value: idValue,
                          });

                          context.updateContext('bookmarks', updatedBookmarks);
                          
                          postManuscriptLibrary({
                            variables: {
                              doc_id: context.documentId,
                              bookmark_id: bookmarkID,
                            },
                            onCompleted: ({ postManuscriptLibrary }) => {
                              if (postManuscriptLibrary.status === 'success') {
                                context.updateContext(
                                  'manuscriptLibrary',
                                  postManuscriptLibrary.response
                                );
                                if (!modular) {
                                  context.updateContext('loadingRequest', false);
                                  context.updateContext('loadingMessage', 'Added to library');
                                }
                              } else {
                                notify(postManuscriptLibrary.message);
                                context.updateContext('loadingRequest', false);
                                context.updateContext(
                                  'loadingMessage',
                                  'Could not add paper to library, try again later'
                                );
                              }
                            },
                          });


                          if (modular) {
                            setRefresher(refresher + 1);
                            context.updateContext('bookmarksRefresher', refresher);
                          }
                        } else {
                          notify(saveBookmark.message);
                          context.updateContext('loadingRequest', false);
                          context.updateContext('loadingMessage', '');
                        }
                      },
                    });
                  }}
                >
                  <AddBoxOutlinedIcon sx={{ width: 16, height: 16, marginRight: '10px' }} />
                  Add paper to Library
                </MenuItem>
              :
                <MenuItem
                  onClick={async (e) => {
                    handleMenuClose();
                    e.preventDefault();
                    setModalRemovePromptNoteLibrary(true);
                  }}
                >
                  <DisabledByDefaultOutlinedIcon sx={{ width: 16, height: 16, marginRight: '10px' }} />
                  Remove paper from Library
                </MenuItem>

              }

              <MenuItem
                onClick={async () => {
                  handleMenuClose();

                  if (!modular) {
                    context.updateContext('loadingRequest', true);
                    context.updateContext('loadingMessage', 'Retrieving citations');
                  }

                  const {
                    collection,
                    id_value: idValue,
                    id_field: idField,
                    id_type: idType,
                  } = context[modular ? 'metadataResults' : 'paperIds'].find(
                    (meta) => meta.id_value === paper.id_int.toString()
                  );

                  citePaper({
                    variables: {
                      paper_id: {
                        collection,
                        id_value: idValue,
                        id_field: idField,
                        id_type: idType,
                      },
                    },
                  });
                }}
              >
                <FileDownloadIcon sx={{ width: 16, height: 16, marginRight: '10px' }} />
                Generate paper citations
              </MenuItem>
            </Menu>
            
          </ButtonGroup>
        </Stack>   

        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              flexGrow: 1,
            }}
          >
            Abstract:&nbsp;
            <Box
              component="span"
              sx={{
                fontWeight: 'normal',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                flexGrow: 1,
              }}
            >
            {paper?.Content?.Abstract_Parsed?.[0]?.section_text?.[0]?.paragraph_text?.[0]?.sentence_text ?? 'No abstract available'}
            </Box>
          </Box>
          <Button
            variant="text"
            disableRipple
            sx={{
              minWidth: 0,
              textTransform: 'none',
              fontSize: 'inherit',
              color: 'cornflowerblue',
              border: 'none',
              whiteSpace: 'nowrap',
              flexShrink: 0, // Ensure button does not shrink
              ml: 1,
              '&:hover': { backgroundColor: 'transparent' },
              '&:focus': { backgroundColor: 'transparent' },
            }}
            onClick={() => { 
              collapsedToggle(!collapsed);
            }}
          >
            Read more
          </Button>
        </Typography>
                  
        <Collapse in={collapsed} timeout="auto" unmountOnExit>
          <AbstractParsed paper={paper} modular={modular} highlighter={''} />
        </Collapse>
        {paper.relevant_sentence !== undefined &&
          paper.relevant_sentences.map((sentence, index) => (
            <Box
              key={index}
              sx={{
                border: '1px solid lightgray',
                marginBottom: '5px',
                fontSize: '14px',
                padding: '5px',
                fontFamily: 'Arial',
              }}
            >
              <Highlighter
                key={index}
                autoEscape={false}
                highlightClassName={{
                  fontWeight: 'bold',
                }}
                highlightStyle={{
                  color: 'indianred',
                  backgroundColor: 'transparent',
                }}
                searchWords={
                  !modular && context.keywords
                    ? keywordRegex(context.keywords)
                    : context.keywords // ELSE POINT TO MODULAR KEYWORDS
                }
                textToHighlight={sentence}
              />
            </Box>
          ))}
      </CardContent>
    </Card>
    </>
  );
};

const keywordRegex = (keywords) => {
  const keywordList = keywords.join('|');
  const re = new RegExp(`\\b(${keywordList})\\b`, 'gm');
  return [re];
};

export default SearchResult;